import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useQuery } from '@tanstack/vue-query'

import { BrandedAppService } from '../services/branded-app.service'
import type { BrandedAppMetadata } from '../types/branded-app'

export const brandedAppQueryKeys = {
  all: () => ['brandedApp'] as const,
  metadata: (locationId: string) => [...brandedAppQueryKeys.all(), 'metadata', locationId] as const,
}

const isNotFoundError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false
  const status = (error as { status?: number; statusCode?: number }).status ?? (error as { statusCode?: number }).statusCode
  return status === 404
}

export const useBrandedAppQueryStore = () => {
  const service = new BrandedAppService()

  /**
   * Public branded-app metadata (store links, package id, app icon) for a location.
   * `enabled` lets callers skip the request where it is not needed (e.g. desktop).
   * Resolves to `null` when the location has no branded app.
   */
  const fetchBrandedAppMetadataQuery = (locationId: MaybeRefOrGetter<string | undefined>, enabled: MaybeRefOrGetter<boolean>) => {
    const id = () => toValue(locationId)?.trim() ?? ''

    return useQuery<BrandedAppMetadata | null>({
      queryKey: computed(() => brandedAppQueryKeys.metadata(id())),
      enabled: computed(() => !!id() && toValue(enabled)),
      // Store links do not change within a session.
      staleTime: Infinity,
      queryFn: async () => {
        try {
          return await service.fetchMetadata(id())
        } catch (error) {
          if (isNotFoundError(error)) return null
          throw error
        }
      },
    })
  }

  return { fetchBrandedAppMetadataQuery }
}
